import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
    Calendar,
    FileText,
    BarChart3,
    Users,
    Bot,
    CheckCircle2,
    ShieldCheck,
    Globe,
    Zap,
    ArrowRight,
    Clock,
    TrendingUp,
    Smartphone,
    Play
} from 'lucide-react';
import './LandingPage.css';

// Importing assets
import tmsLogo from '../../assets/TmsLogoh.png';
import dashboardImg from '../../assets/dashboard.png';
import mobilePreviewImg from '../../assets/mobile.png';
import heroImg from '../../assets/landing_page.png';
import chauffeurAppDashboard from '../../assets/chauffeur_app_dashboard_picture.png';
import chauffeurUtiliseApp from '../../assets/chauffeur_utilise_app.png';

const LandingPage = () => {
    const [scrolled, setScrolled] = useState(false);

    useEffect(() => {
        const handleScroll = () => {
            const isScrolled = window.scrollY > 20;
            if (isScrolled !== scrolled) {
                setScrolled(isScrolled);
            }
        };

        document.addEventListener('scroll', handleScroll, { passive: true });

        const observerOptions = {
            threshold: 0.1,
            rootMargin: '0px 0px -50px 0px'
        };

        const observer = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    entry.target.classList.add('active');
                }
            });
        }, observerOptions);

        const revealElements = document.querySelectorAll('.reveal');
        revealElements.forEach(el => observer.observe(el));

        return () => {
            document.removeEventListener('scroll', handleScroll);
            observer.disconnect();
        };
    }, [scrolled]);

    return (
        <div className="landing-container">
            {/* Navbar */}
            <nav className={`landing-nav ${scrolled ? 'nav-scrolled' : ''}`}>
                <div className="nav-container">
                    <div className="nav-logo">
                        <img src={tmsLogo} alt="Bfret Logo" />
                    </div>
                    <div className="nav-links">
                        <div className="nav-dropdown">
                            <span className="nav-dropdown-toggle">Solutions</span>
                            <div className="nav-dropdown-menu">
                                <Link to="/solutions/planification-intelligente">Planification intelligente</Link>
                                <Link to="/solutions/suivi-temps-reel">Suivi en temps réel</Link>
                                <Link to="/solutions/documents-centralises">Documents centralisés</Link>
                                <Link to="/solutions/rapports-analytics">Rapports & Analytics</Link>
                                <Link to="/solutions/gestion-ressources">Gestion des ressources</Link>
                                <Link to="/solutions/ai-agent-center">AI Agent Center</Link>
                            </div>
                        </div>
                        <a href="#features">Fonctionnalités</a>
                        <a href="#benefits">Avantages</a>
                        <Link to="/contact">Contact</Link>
                    </div>
                    <div className="nav-actions">
                        <Link to="/login" className="btn-text">Se connecter</Link>
                        <Link to="/contact" className="btn-primary">Démo</Link>
                    </div>
                </div>
            </nav>

            {/* Hero Section */}
            <section className="hero-section">
                <div className="hero-overlay"></div>
                <div className="hero-content-wrapper">
                    <div className="hero-content reveal reveal-up">
                        <div className="hero-badge">
                            <span className="badge-dot"></span>
                            TMS nouvelle génération
                        </div>
                        <h1>
                            Simplifiez. Planifiez.
                            <br />
                            <span className="text-gradient">Suivez. Livrez.</span>
                        </h1>
                        <p className="hero-subtitle">
                            BFRET TMS centralise la gestion de vos transports, de la planification 
                            des missions au suivi de livraison en temps réel.
                        </p>
                        <div className="hero-cta">
                            <Link to="/contact" className="btn-primary btn-large">
                                Demander une démo
                                <ArrowRight size={18} />
                            </Link>
                            <a href="#features" className="btn-secondary btn-large">
                                <Play size={18} />
                                Découvrir
                            </a>
                        </div>
                    </div>
                    
                    {/*<div className="hero-image-container reveal reveal-up delay-1">
                        <img 
                            src={chauffeurAppDashboard} 
                            alt="Bfret Dashboard Preview" 
                            className="hero-dashboard-img" 
                        />
                    </div>*/}
                </div>
            </section>

            {/* Trusted By 
            <section className="trusted-by-section">
                <div className="container">
                    <p className="trusted-label">Ils nous font confiance</p>
                    <div className="trusted-logos">
                        <span className="logo-item">DISLOG GROUP</span>
                        <span className="logo-item">LOGISTIQA</span>
                        <span className="logo-item">TRANS EXPRESS</span>
                        <span className="logo-item">FASTFRET</span>
                        <span className="logo-item">MOVE LOGISTICS</span>
                        <span className="logo-item">NORD SUD</span>
                    </div>
                </div>
            </section>*/}

            {/* Features */}
            <section id="features" className="features-section section-padding">
                <div className="container">
                    <div className="section-header reveal reveal-up">
                        <span className="section-badge">Fonctionnalités</span>
                        <h2>Une plateforme complète<br />pour vos opérations</h2>
                    </div>

                    <div className="features-grid">
                        <div className="feature-card reveal reveal-up">
                            <div className="feature-icon icon-blue">
                                <Calendar size={24} />
                            </div>
                            <h3>Planification intelligente</h3>
                            <p>Planifiez et assignez vos missions en quelques clics.</p>
                        </div>

                        <div className="feature-card reveal reveal-up delay-1">
                            <div className="feature-icon icon-indigo">
                                <Zap size={24} />
                            </div>
                            <h3>Suivi en temps réel</h3>
                            <p>Suivez vos véhicules et chauffeurs en temps réel.</p>
                        </div>

                        <div className="feature-card reveal reveal-up delay-2">
                            <div className="feature-icon icon-orange">
                                <FileText size={24} />
                            </div>
                            <h3>Documents centralisés</h3>
                            <p>Accédez à tous vos documents au même endroit.</p>
                        </div>

                        <div className="feature-card reveal reveal-up">
                            <div className="feature-icon icon-purple">
                                <BarChart3 size={24} />
                            </div>
                            <h3>Rapports & Analytics</h3>
                            <p>Analysez vos performances avec des KPI détaillés.</p>
                        </div>

                        <div className="feature-card reveal reveal-up delay-1">
                            <div className="feature-icon icon-green">
                                <Users size={24} />
                            </div>
                            <h3>Gestion des ressources</h3>
                            <p>Gérez chauffeurs, véhicules et clients facilement.</p>
                        </div>

                        <div className="feature-card reveal reveal-up delay-2">
                            <div className="feature-icon icon-pink">
                                <Bot size={24} />
                            </div>
                            <h3>AI Agent Center</h3>
                            <p>Automatisez vos tâches avec l'intelligence artificielle.</p>
                        </div>
                    </div>
                </div>
            </section>

            {/* Benefits */}
            <section id="benefits" className="benefits-section section-padding bg-dark">
                <div className="container">
                    <div className="section-header reveal reveal-up">
                        <span className="section-badge section-badge-white">Avantages</span>
                        <h2 className="text-white">Pourquoi BFRET TMS ?</h2>
                    </div>

                    <div className="benefits-grid">
                        <div className="benefit-card reveal reveal-up">
                            <div className="benefit-icon">
                                <TrendingUp size={24} />
                            </div>
                            <h3>Réduction des coûts</h3>
                            <p>Optimisez vos tournées et réduisez les coûts jusqu'à 25%.</p>
                        </div>
                        <div className="benefit-card reveal reveal-up delay-1">
                            <div className="benefit-icon">
                                <Clock size={24} />
                            </div>
                            <h3>Gain de temps</h3>
                            <p>Automatisez les tâches et gagnez 30% de temps productif.</p>
                        </div>
                        <div className="benefit-card reveal reveal-up delay-2">
                            <div className="benefit-icon">
                                <ShieldCheck size={24} />
                            </div>
                            <h3>Sécurité des données</h3>
                            <p>Vos données sont sécurisées au plus haut niveau.</p>
                        </div>
                    </div>
                </div>
            </section>

            {/* Dashboard Showcase - UPDATED with larger image */}
            <section className="showcase-section section-padding bg-light">
                <div className="container">
                    <div className="showcase-container">
                        <div className="showcase-content reveal reveal-left">
                            <span className="section-badge">Tableau de bord</span>
                            <h2>Pilotez votre activité<br />en temps réel</h2>
                            <p className="showcase-description">
                                Une vue complète de vos opérations pour suivre l'essentiel.
                            </p>
                            
                            <ul className="showcase-list">
                                <li>
                                    <CheckCircle2 size={20} className="icon-success" />
                                    Suivi des missions en cours
                                </li>
                                <li>
                                    <CheckCircle2 size={20} className="icon-success" />
                                    Statut des livraisons en direct
                                </li>
                                <li>
                                    <CheckCircle2 size={20} className="icon-success" />
                                    Indicateurs de performance clés
                                </li>
                            </ul>
                        </div>
                        <div className="showcase-visual reveal reveal-right">
                            <img src={chauffeurAppDashboard} alt="Dashboard" className="showcase-img" />
                        </div>
                    </div>
                </div>
            </section>

            {/* Driver App - ELEGANT REDESIGN */}
            <section className="app-section section-padding" style={{ backgroundColor: '#ffffff', borderTop: '1px solid #f1f5f9' }}>
                <div className="container">
                    <div className="saas-feature-grid reverse">
                        <div className="saas-feature-text reveal reveal-right" style={{ paddingLeft: '2rem' }}>
                            <span className="saas-feature-tag">Application Chauffeur</span>
                            <h2 style={{ fontSize: '2.5rem', lineHeight: '1.2', marginBottom: '1.5rem', color: '#0f172a' }}>
                                Vos chauffeurs<br />
                                <span className="text-gradient">parfaitement connectés</span>
                            </h2>
                            <p style={{ fontSize: '1.15rem', color: '#475569', marginBottom: '2.5rem', lineHeight: '1.6' }}>
                                Fini les appels incessants et les documents papier perdus. Offrez à vos équipes terrain une application mobile intuitive, pensée pour faciliter leur quotidien.
                            </p>
                            
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                                <div>
                                    <h3 style={{ fontSize: '1.25rem', display: 'flex', alignItems: 'center', gap: '0.75rem', color: '#0f172a', marginBottom: '0.5rem' }}>
                                        <CheckCircle2 size={24} color="var(--saas-primary)" />
                                        Missions en temps réel
                                    </h3>
                                    <p style={{ fontSize: '1.05rem', color: '#475569', margin: 0, paddingLeft: '2.25rem' }}>
                                        Réception instantanée des plannings et mises à jour des adresses ou consignes.
                                    </p>
                                </div>
                                <div>
                                    <h3 style={{ fontSize: '1.25rem', display: 'flex', alignItems: 'center', gap: '0.75rem', color: '#0f172a', marginBottom: '0.5rem' }}>
                                        <CheckCircle2 size={24} color="var(--saas-primary)" />
                                        Navigation fluide
                                    </h3>
                                    <p style={{ fontSize: '1.05rem', color: '#475569', margin: 0, paddingLeft: '2.25rem' }}>
                                        Guidage GPS intégré pour optimiser les itinéraires et assurer la ponctualité.
                                    </p>
                                </div>
                                <div>
                                    <h3 style={{ fontSize: '1.25rem', display: 'flex', alignItems: 'center', gap: '0.75rem', color: '#0f172a', marginBottom: '0.5rem' }}>
                                        <CheckCircle2 size={24} color="var(--saas-primary)" />
                                        Preuve de livraison
                                    </h3>
                                    <p style={{ fontSize: '1.05rem', color: '#475569', margin: 0, paddingLeft: '2.25rem' }}>
                                        Signature électronique et scan de documents (e-CMR) remontés directement au bureau.
                                    </p>
                                </div>
                            </div>
                        </div>
                        <div className="saas-feature-visual reveal reveal-left" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <div style={{ position: 'relative' }}>
                                <div style={{ position: 'absolute', inset: '-10px', background: 'linear-gradient(to left, #e0e7ff, #f3e8ff)', filter: 'blur(30px)', opacity: '0.6', borderRadius: '20px', zIndex: 0 }}></div>
                                <img 
                                    src={chauffeurUtiliseApp} 
                                    alt="Chauffeur utilisant l'application BFRET" 
                                    style={{ 
                                        position: 'relative',
                                        zIndex: 1,
                                        width: '100%', 
                                        maxWidth: '500px',
                                        borderRadius: '16px', 
                                        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
                                        border: '1px solid rgba(0,0,0,0.05)'
                                    }} 
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Stats */}
            <section className="stats-section">
                <div className="container">
                    <div className="stats-grid">
                        <div className="stat-item reveal reveal-up">
                            <span className="stat-number">-25%</span>
                            <span className="stat-label">Réduction des coûts</span>
                        </div>
                        <div className="stat-item reveal reveal-up delay-1">
                            <span className="stat-number">+30%</span>
                            <span className="stat-label">Productivité</span>
                        </div>
                        <div className="stat-item reveal reveal-up delay-2">
                            <span className="stat-number">98%</span>
                            <span className="stat-label">Livraisons à l'heure</span>
                        </div>
                        <div className="stat-item reveal reveal-up delay-3">
                            <span className="stat-number">4.9★</span>
                            <span className="stat-label">Satisfaction client</span>
                        </div>
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section className="cta-section section-padding">
                <div className="container">
                    <div className="cta-content reveal reveal-up">
                        <h2>Prêt à transformer<br />votre gestion du transport ?</h2>
                        <p>Demandez une démonstration personnalisée dès aujourd'hui.</p>
                        <Link to="/contact" className="btn-primary btn-large">
                            Demander une démo
                            <ArrowRight size={18} />
                        </Link>
                    </div>
                </div>
            </section>

            {/* Footer */}
            <footer className="landing-footer">
                <div className="container">
                    <div className="footer-container">
                        <div className="footer-col-main">
                            <div className="footer-logo-wrapper">
                                <img src={tmsLogo} alt="Bfret Logo" className="footer-logo" />
                            </div>
                            <p>La solution TMS complète pour optimiser la gestion de vos transports.</p>
                        </div>
                        <div className="footer-col">
                            <h4>Solutions</h4>
                            <ul>
                                <li><a href="#">Planification</a></li>
                                <li><a href="#">Suivi en temps réel</a></li>
                                <li><a href="#">Application chauffeur</a></li>
                                <li><a href="#">Analytics</a></li>
                            </ul>
                        </div>
                        {/*<div className="footer-col">
                            <h4>Ressources</h4>
                            <ul>
                                <li><a href="#">Blog</a></li>
                                <li><a href="#">Guides</a></li>
                                <li><a href="#">Centre d'aide</a></li>
                                <li><a href="#">API</a></li>
                            </ul>
                        </div>*/}
                        <div className="footer-col">
                            <h4>Entreprise</h4>
                            <ul>
                                <li><a href="#">À propos</a></li>
                                <li><a href="#">Contact</a></li>
                                <li><a href="#">Partenaires</a></li>
                            </ul>
                        </div>
                    </div>
                    <div className="footer-bottom">
                        <span>&copy; 2024 BFRET TMS. Tous droits réservés.</span>
                        <div className="footer-legal">
                            <Link to="/mentions-legales">Mentions légales</Link>
                            <Link to="/confidentialite">Confidentialité</Link>
                            <Link to="/cgu">CGU</Link>
                        </div>
                    </div>
                </div>
            </footer>
        </div>
    );
};

export default LandingPage;